import { useParams } from 'react-router-dom';

import Storage from 'storage';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useToast,
  Tooltip,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  useDisclosure,
  ModalCloseButton,
} from '@chakra-ui/react';

import { apiSendTokenXCreditCardEmail } from 'api';
import { BsSend } from 'react-icons/bs';
import { CheckCircleIcon, ExternalLinkIcon } from '@chakra-ui/icons';

interface PaymentLinkProps {
  paymentMethod: 'eft' | 'credit_card';
  section: 'payment_method' | 'down_payment';
  accountHolderEmail?: string;
  isDataSaved: boolean;
  payments?: Array<{
    type_name: string;
    section_name: string;
    [key: string]: any;
  }>;
}

const PaymentLink = ({
  paymentMethod,
  section,
  accountHolderEmail,
  isDataSaved,
  payments,
}: PaymentLinkProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { uuid } = useParams<{ uuid: string }>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isIframeOpen,
    onOpen: onIframeOpen,
    onClose: onIframeClose,
  } = useDisclosure();

  // Generate EFT payment URL using backend API endpoint
  const apiEndpoint = Storage.get('API_ENDPOINT');
  const eftPaymentUrl =
    uuid && apiEndpoint
      ? `${apiEndpoint}tokenx/eft/${uuid}/payment_method`
      : null;

  // Check if payment details have already been provided
  const hasPaymentDetails = (() => {
    if (!payments || payments.length === 0) return false;

    return payments.some(
      (payment) =>
        payment.type_name === paymentMethod && payment.section_name === section,
    );
  })();

  const { mutate: sendSecurePaymentLink, isPending: isSendingPaymentLink } =
    useMutation({
      mutationFn: (params: {
        type: 'credit_card' | 'eft';
        section: 'payment_method' | 'down_payment';
      }) => apiSendTokenXCreditCardEmail(uuid!, params),
      onSuccess: () => {
        onClose();
        toast({
          title: 'Success',
          description: 'Secure payment link sent successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      },
      onError: (error: any) => {
        onClose();
        const errorMessage =
          error?.response?.data?.message ||
          'Failed to send secure payment link';
        toast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      },
    });

  const handleLinkClick = () => {
    if (
      !uuid ||
      !paymentMethod ||
      !isDataSaved ||
      hasPaymentDetails ||
      !accountHolderEmail
    )
      return;
    onOpen();
  };

  const handleSendConfirm = () => {
    sendSecurePaymentLink({
      type: paymentMethod,
      section,
    });
  };

  const handleIframeClose = () => {
    onIframeClose();

    queryClient.invalidateQueries({
      queryKey: ['application-payments', uuid],
    });
  };

  const getButtonText = () => {
    if (hasPaymentDetails) return 'Payment Info Provided';
    if (isSendingPaymentLink) return 'Sending...';
    return 'Send Secure Payment Link';
  };

  const getButtonProps = () => {
    if (hasPaymentDetails) {
      return {
        colorScheme: 'green',
        variant: 'solid',
        leftIcon: <CheckCircleIcon />,
        isDisabled: true,
        _disabled: {
          opacity: 1,
          cursor: 'default',
        },
      };
    }

    if (!isDataSaved || !accountHolderEmail) {
      return {
        colorScheme: 'gray',
        variant: 'outline',
        leftIcon: <BsSend />,
        isDisabled: true,
        title: !accountHolderEmail
          ? 'Account holder email is required'
          : 'Save form changes to enable',
      };
    }

    return {
      colorScheme: 'teal',
      variant: 'solid',
      leftIcon: <BsSend />,
      isDisabled: isSendingPaymentLink,
    };
  };

  return (
    <Box mt="10px">
      <Tooltip
        label={
          hasPaymentDetails
            ? 'Payment details have already been provided'
            : !accountHolderEmail
            ? 'Account holder email is required'
            : !isDataSaved
            ? 'Save form changes to enable'
            : undefined
        }
        placement="top"
        hasArrow
        isDisabled={isDataSaved && !hasPaymentDetails && !!accountHolderEmail}
      >
        <Box display="inline-block">
          <Button
            {...getButtonProps()}
            onClick={
              !hasPaymentDetails && isDataSaved && accountHolderEmail
                ? handleLinkClick
                : undefined
            }
            size="sm"
            fontWeight="medium"
            mb={2}
          >
            {getButtonText()}
          </Button>
        </Box>
      </Tooltip>
      {paymentMethod === 'eft' && eftPaymentUrl && !hasPaymentDetails && (
        <Tooltip
          label={!isDataSaved ? 'Save form changes to enable' : undefined}
          placement="top"
          hasArrow
          isDisabled={isDataSaved}
        >
          <Box display="inline-block">
            <Button
              colorScheme={isDataSaved ? 'teal' : 'gray'}
              variant={isDataSaved ? 'solid' : 'outline'}
              leftIcon={<ExternalLinkIcon />}
              onClick={isDataSaved ? onIframeOpen : undefined}
              size="sm"
              fontWeight="medium"
              isDisabled={!isDataSaved}
              pointerEvents={!isDataSaved ? 'none' : 'auto'}
            >
              Add EFT Data Manually
            </Button>
          </Box>
        </Tooltip>
      )}

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send Secure Payment Link</ModalHeader>
          <ModalBody>
            <Text>
              Are you sure you want to send a secure payment link for{' '}
              <strong>
                {section === 'payment_method'
                  ? 'payment method'
                  : 'down payment'}
              </strong>{' '}
              to the following email address?
            </Text>
            <Text mt="15px" fontWeight="semibold" color="teal.600">
              {accountHolderEmail}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              onClick={handleSendConfirm}
              isLoading={isSendingPaymentLink}
              loadingText="Sending..."
            >
              Send
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* EFT Payment Iframe Modal */}
      <Modal
        isOpen={isIframeOpen}
        onClose={handleIframeClose}
        size="4xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent m={0}>
          <ModalHeader>Add EFT Payment Information</ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0} m={0}>
            {eftPaymentUrl && (
              <Box
                as="iframe"
                src={eftPaymentUrl}
                width="100%"
                height="90dvh"
                border="none"
                title="EFT Payment Form"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PaymentLink;
